import { useCallback, useState, useRef, useLayoutEffect, useMemo } from 'react';

import { ButtonGroup } from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import { Code } from '@atlaskit/code';
import CopyIcon from '@atlaskit/icon/core/migration/copy';
import { Box, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';

// tooltip text for the copy button
const TOOLTIP_MESSAGE = {
  PROMPT: 'Copy',
  SUCCESS: 'Copied!',
  FAILURE: 'Copy to clipboard failed',
} as const;

const wrapperStyles = xcss({
  display: 'flex',
  alignItems: 'center',
});

const idCodeStyles = xcss({
  padding: 'space.050',
});

const codeAndButtonWrapper = xcss({
  display: 'flex',
  alignItems: 'center',
});

const writeToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};
/**
 * CopyActionButton
 * A button that copies the provided source code to the clipboard.
 * It also provides a tooltip to indicate the copy status.
 * @param source - The source code to copy.
 * @param onCopyButtonClicked - Optional analytics event handler.
 * @returns A button that copies the source code to the clipboard.
 */
const CopyActionButton = ({
  source,
  onCopyButtonClicked,
}: {
  source: string; // The source code to copy
  onCopyButtonClicked?: () => void; // Optional analytics event handler
}) => {
  const [tooltipMessage, setTooltipMessage] = useState<string>(
    TOOLTIP_MESSAGE.PROMPT
  );
  const updateTooltip = useRef<() => void>();

  // Check if clipboard can be used
  const isClipboardSupported = useMemo(
    () => typeof navigator !== 'undefined' && !!navigator.clipboard?.writeText,
    []
  );

  const handleCopyButtonClick = useCallback(async () => {
    const success = await writeToClipboard(source);
    setTooltipMessage(
      success ? TOOLTIP_MESSAGE.SUCCESS : TOOLTIP_MESSAGE.FAILURE
    );
    if (onCopyButtonClicked) {
      onCopyButtonClicked();
    }
  }, [source, onCopyButtonClicked]);

  // Reset tooltip to "Copy" on hover or focus
  const resetTooltipText = useCallback(() => {
    setTooltipMessage(TOOLTIP_MESSAGE.PROMPT);
  }, []);

  // Update the tooltip when the message changes
  useLayoutEffect(() => {
    updateTooltip.current?.();
  }, [tooltipMessage]);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => event.stopPropagation(),
    []
  );

  // Don't render the button if clipboard is not supported for some reason
  if (!isClipboardSupported) {
    return null;
  }

  return (
    <Box onMouseDown={handleMouseDown}>
      <ButtonGroup label="actions">
        <Tooltip
          content={({ update }) => {
            updateTooltip.current = update;
            return tooltipMessage;
          }}
          position="top"
        >
          <IconButton
            icon={CopyIcon}
            label={tooltipMessage}
            onClick={handleCopyButtonClick}
            onMouseOver={resetTooltipText}
            onFocus={resetTooltipText}
            appearance="subtle"
            spacing="compact"
            name="Copy"
            aria-live="assertive"
            aria-atomic="true"
          />
        </Tooltip>
      </ButtonGroup>
    </Box>
  );
};

type CodeBlockWithCopyButtonProps = {
  source: string;
  onCopyButtonClicked?: () => void;
};

/**
 * CopyableErrorCode
 * A code block component that displays the provided source code and includes a copy button.
 * It allows users to easily copy the code to the clipboard.
 * @param source - The source code to display.
 * @param onCopyButtonClicked - Optional analytics event handler.
 * @returns A code block with a copy button.
 */
export const CopyableErrorCode = ({
  source,
  onCopyButtonClicked,
}: CodeBlockWithCopyButtonProps) => (
  <Box xcss={wrapperStyles}>
    <Code>
      <Box xcss={codeAndButtonWrapper}>
        {/* show the actual code to be copied */}
        <Box as="div" xcss={idCodeStyles}>
          {source}
        </Box>
        <CopyActionButton
          source={source}
          onCopyButtonClicked={onCopyButtonClicked}
        />
      </Box>
    </Code>
  </Box>
);
